import store from "src/Redux/Store";
import axiosInstance from "src/Funcs/axiosFuncs/axiosInstance";
import { createUploadStallGuard } from "src/Funcs/APIFuncs/uploadStallGuard";
import {
  getSocketUserChat,
  emitWithCallback,
  emitWhenConnected,
} from "src/Funcs/APIFuncs/socketUserChat";

import { API } from "src/Funcs/APIFuncs/APIFunc";

import {
  GROUP_GET_LIST_REQUEST,
  GROUP_GET_LIST_SUCCESS,
  GROUP_GET_LIST_FAIL,
  GROUP_GET_BY_ID_REQUEST,
  GROUP_GET_BY_ID_SUCCESS,
  GROUP_GET_BY_ID_FAIL,
  GROUP_CREATE_REQUEST,
  GROUP_CREATE_SUCCESS,
  GROUP_CREATE_FAIL,
  GROUP_SEND_MESSAGE_REQUEST,
  GROUP_SEND_MESSAGE_SUCCESS,
  GROUP_SEND_MESSAGE_FAIL,
  GROUP_VOICE_UPLOAD_COMPLETE,
  GROUP_NEW_MESSAGE,
  GROUP_SELECT,
  GROUP_LEAVE_ROOM,
  GROUP_RESET,
  GROUP_ADD_MEMBER_SUCCESS,
  GROUP_REMOVE_MEMBER_SUCCESS,
  GROUP_SET_ADMIN_SUCCESS,
  GROUP_ADD_TOPIC_SUCCESS,
  GROUP_UPDATE_TOPIC_SUCCESS,
  GROUP_DELETE_TOPIC_SUCCESS,
  GROUP_DELETE_MESSAGE_SUCCESS,
  GROUP_BULK_DELETE_MESSAGES_SUCCESS,
  GROUP_LEAVE_SUCCESS,
  GROUP_PIN_MESSAGE_SUCCESS,
  GROUP_UNPIN_MESSAGE_SUCCESS,
  GROUP_EDIT_MESSAGE_SUCCESS,
  GROUP_SET_SELECTED_TOPIC,
  GROUP_CALL_ACTIVE,
  GROUP_CALL_ENDED,
  GROUP_SET_UNREAD_COUNTS,
  GROUP_MARK_SEEN,
  GROUP_REMOTE_SEEN,
  GROUP_MESSAGE_REACTION,
  GROUP_TOGGLE_PUBLIC_SUCCESS,
  GROUP_GENERATE_LINK_SUCCESS,
  GROUP_PREVIEW_BY_LINK_REQUEST,
  GROUP_PREVIEW_BY_LINK_SUCCESS,
  GROUP_PREVIEW_BY_LINK_FAIL,
  GROUP_JOIN_BY_LINK_SUCCESS,
  GROUP_JOIN_REQUESTS_SUCCESS,
  GROUP_PROCESS_JOIN_REQUEST_SUCCESS,
  GROUP_UPDATE_INFO_SUCCESS,
  GROUP_BULK_MARK,
  GROUP_BULK_MUTE,
  GROUP_BULK_REMOVE,
  GROUP_LOAD_OLDER_MESSAGES_REQUEST,
  GROUP_LOAD_OLDER_MESSAGES_SUCCESS,
  GROUP_LOAD_OLDER_MESSAGES_FAIL,
} from "./groupChatTypes";

export const getGroupList =
  (query = {}) =>
  async (dispatch) => {
    dispatch({ type: GROUP_GET_LIST_REQUEST });
    try {
      const payload = await emitWithCallback("group:getList", { query });
      dispatch({ type: GROUP_GET_LIST_SUCCESS, payload });
      const items = payload?.body?.items ?? [];
      items.forEach((group) => {
        const groupId = group?._id;
        if (groupId) {
          emitWhenConnected("group:joinRoom", {
            groupId: String(groupId),
          }).catch(() => {});
        }
      });
    } catch (err) {
      dispatch({
        type: GROUP_GET_LIST_FAIL,
        payload: err?.message ?? "Failed to load groups",
      });
    }
  };

export const getGroupById =
  (groupId, query = { page: 1, limit: 30 }) =>
  async (dispatch) => {
    dispatch({ type: GROUP_GET_BY_ID_REQUEST });
    try {
      const payload = await emitWithCallback("group:getById", {
        params: { groupId: String(groupId) },
        query,
      });

      dispatch({ type: GROUP_GET_BY_ID_SUCCESS, payload });
    } catch (err) {
      dispatch({
        type: GROUP_GET_BY_ID_FAIL,
        payload: err?.message ?? "Failed to load group",
      });
    }
  };

export const loadOlderGroupMessages =
  (groupId, page = 2) =>
  async (dispatch) => {
    dispatch({ type: GROUP_LOAD_OLDER_MESSAGES_REQUEST });
    try {
      const payload = await emitWithCallback("group:getById", {
        params: { groupId: String(groupId) },
        query: { page, limit: 30 },
      });
      dispatch({
        type: GROUP_LOAD_OLDER_MESSAGES_SUCCESS,
        payload: { ...payload, _groupId: groupId },
      });
    } catch (err) {
      const message = err?.message ?? "Failed to load older messages";
      dispatch({ type: GROUP_LOAD_OLDER_MESSAGES_FAIL, payload: message });
    }
  };

export const createGroup = (body) => async (dispatch) => {
  dispatch({ type: GROUP_CREATE_REQUEST });
  try {
    const payload = await emitWithCallback("group:create", { body });
    dispatch({ type: GROUP_CREATE_SUCCESS, payload });
    const group = payload?.body?.group;
    if (group?._id) {
      emitWhenConnected("group:joinRoom", { groupId: String(group._id) }).catch(
        () => {},
      );
    }
    return payload?.body?.group;
  } catch (err) {
    dispatch({
      type: GROUP_CREATE_FAIL,
      payload: err?.message ?? "Failed to create group",
    });
    throw err;
  }
};

export const selectGroup = (groupId) => (dispatch) => {
  dispatch({ type: GROUP_SELECT, payload: groupId });
};

export const joinGroupRoom = (groupId) => () => {
  const id = groupId != null ? String(groupId) : null;
  if (!id) return;
  emitWhenConnected("group:joinRoom", { groupId: id }).catch(() => {});
};

export const leaveGroupRoom = (groupId) => (dispatch) => {
  const id = groupId != null ? String(groupId) : null;
  if (!id) return;
  const socket = getSocketUserChat();
  if (socket) socket.emit("group:leaveRoom", { groupId: id });
  dispatch({ type: GROUP_LEAVE_ROOM, payload: id });
};

export const setSelectedTopic = (topicId) => (dispatch) => {
  dispatch({ type: GROUP_SET_SELECTED_TOPIC, payload: topicId ?? null });
};

export const sendGroupMessage = (groupId, body) => async (dispatch) => {
  const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const currentUserId = localStorage.getItem("UserID");

  const isVoiceUpload = body.voice === true && body._voiceBlob instanceof Blob;

  const tempMessage = {
    _id: tempId,
    Text: body.text ?? "",
    MessageBody: body.text ?? "",
    Attachments: body.attachments ?? [],
    MessageAttachs: body.attachments ?? [],
    Sender: currentUserId,
    CreatedAt: Date.now(),
    Time: Date.now(),
    _status: isVoiceUpload ? "uploading" : "sending",
  };
  if (body.replyTo) {
    tempMessage.ReplyTo = body.replyTo;
    tempMessage.replyTo = body.replyTo;
  }
  dispatch({
    type: GROUP_SEND_MESSAGE_REQUEST,
    payload: { tempMessage, groupId },
  });

  if (isVoiceUpload) {
    try {
      const voiceFile = new File([body._voiceBlob], "voice.webm", {
        type: body._voiceBlob.type || "audio/webm",
      });
      const remoteUrl = await uploadGroupFile(groupId, voiceFile)();
      dispatch({
        type: GROUP_VOICE_UPLOAD_COMPLETE,
        payload: { tempId, remoteUrl, groupId },
      });
      const requestBody = {
        text: body.text || "",
        attachments: [remoteUrl],
        ...(body.topicId != null && body.topicId !== "" && body.topicId !== "__general__"
          ? { topicId: body.topicId }
          : {}),
        ...(body.replyTo ? { replyTo: body.replyTo } : {}),
      };
      const payload = await emitWithCallback("group:sendMessage", {
        params: { groupId: String(groupId) },
        body: requestBody,
      });
      dispatch({
        type: GROUP_SEND_MESSAGE_SUCCESS,
        payload: { ...payload, _optimisticTempId: tempId },
      });
    } catch (err) {
      dispatch({
        type: GROUP_SEND_MESSAGE_FAIL,
        payload: {
          error: err?.message ?? "Failed to send voice message",
          tempId,
        },
      });
    }
    return;
  }

  try {
    const payload = await emitWithCallback("group:sendMessage", {
      params: { groupId: String(groupId) },
      body,
    });
    dispatch({
      type: GROUP_SEND_MESSAGE_SUCCESS,
      payload: { ...payload, _optimisticTempId: tempId },
    });
  } catch (err) {
    dispatch({
      type: GROUP_SEND_MESSAGE_FAIL,
      payload: {
        error: err?.message ?? "Failed to send message",
        tempId,
      },
    });
  }
};

/**
 * Upload a file for the given group. Returns URL for use in sendGroupMessage attachments.
 * @param {string} groupId
 * @param {File} file
 * @returns {Promise<string>} public URL for the uploaded file
 */
export const uploadGroupFile = (groupId, file) => async () => {
  const formData = new FormData();
  formData.append("file", file);
  const url = `${API}groupChats/${String(groupId)}/upload`;
  const guard = createUploadStallGuard();
  try {
    const { data } = await axiosInstance.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      signal: guard.signal,
    });
    const body = data?.body ?? data;
    const uploadedUrl = body?.url;
    if (!uploadedUrl) throw new Error("No url in upload response");
    return uploadedUrl;
  } finally {
    guard.done();
  }
};

export const addGroupMember = (groupId, userId) => async (dispatch) => {
  try {
    const payload = await emitWithCallback("group:addMember", {
      params: { groupId: String(groupId) },
      body: { userId: String(userId) },
    });
    dispatch({ type: GROUP_ADD_MEMBER_SUCCESS, payload });
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const removeGroupMember = (groupId, userId) => async (dispatch) => {
  try {
    const payload = await emitWithCallback("group:removeMember", {
      params: { groupId: String(groupId), userId: String(userId) },
    });
    dispatch({ type: GROUP_REMOVE_MEMBER_SUCCESS, payload });
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const setGroupAdmin = (groupId, userId, isAdmin) => async (dispatch) => {
  try {
    const payload = await emitWithCallback("group:setAdmin", {
      params: { groupId: String(groupId) },
      body: { userId: String(userId), isAdmin: !!isAdmin },
    });
    dispatch({ type: GROUP_SET_ADMIN_SUCCESS, payload });
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const addGroupTopic = (groupId, name) => async (dispatch) => {
  try {
    const payload = await emitWithCallback("group:addTopic", {
      params: { groupId: String(groupId) },
      body: { name: String(name).trim() },
    });
    dispatch({ type: GROUP_ADD_TOPIC_SUCCESS, payload });
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const updateGroupTopic =
  (groupId, topicId, name) => async (dispatch) => {
    try {
      const payload = await emitWithCallback("group:updateTopic", {
        params: { groupId: String(groupId), topicId: String(topicId) },
        body: { name: String(name).trim() },
      });
      dispatch({ type: GROUP_UPDATE_TOPIC_SUCCESS, payload });
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

export const deleteGroupTopic = (groupId, topicId) => async (dispatch) => {
  try {
    const payload = await emitWithCallback("group:deleteTopic", {
      params: { groupId: String(groupId), topicId: String(topicId) },
    });
    dispatch({ type: GROUP_DELETE_TOPIC_SUCCESS, payload });
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const deleteGroupMessage =
  (groupId, messageId, hard = false) =>
  async (dispatch) => {
    try {
      const payload = await emitWithCallback("group:deleteMessage", {
        params: { groupId: String(groupId), messageId: String(messageId) },
        query: hard ? { hard: "true" } : {},
      });
      dispatch({ type: GROUP_DELETE_MESSAGE_SUCCESS, payload });
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

export const bulkDeleteGroupMessages =
  (groupId, messageIds, hard = false) =>
  async (dispatch) => {
    try {
      const payload = await emitWithCallback("group:bulkDeleteMessages", {
        params: { groupId: String(groupId) },
        body: { messageIds: messageIds.map(String) },
        query: hard ? { hard: "true" } : {},
      });
      dispatch({ type: GROUP_BULK_DELETE_MESSAGES_SUCCESS, payload });
      return payload;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

export const pinGroupMessage = (groupId, messageId) => async (dispatch) => {
  try {
    const payload = await emitWithCallback("group:pinMessage", {
      params: { groupId: String(groupId), messageId: String(messageId) },
    });
    dispatch({ type: GROUP_PIN_MESSAGE_SUCCESS, payload });
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const unpinGroupMessage = (groupId, messageId) => async (dispatch) => {
  try {
    const payload = await emitWithCallback("group:unpinMessage", {
      params: { groupId: String(groupId), messageId: String(messageId) },
    });
    dispatch({ type: GROUP_UNPIN_MESSAGE_SUCCESS, payload });
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const editGroupMessage =
  (groupId, messageId, text) => async (dispatch) => {
    try {
      const payload = await emitWithCallback("group:editMessage", {
        params: { groupId: String(groupId), messageId: String(messageId) },
        body: { text: String(text).trim() },
      });
      dispatch({ type: GROUP_EDIT_MESSAGE_SUCCESS, payload });
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

export const leaveGroup = (groupId) => async (dispatch) => {
  try {
    const payload = await emitWithCallback("group:leave", {
      params: { groupId: String(groupId) },
    });
    dispatch({ type: GROUP_LEAVE_SUCCESS, payload });
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const markGroupSeen =
  (groupId, messageIds = null) =>
  async (dispatch) => {
    try {
      await emitWithCallback("group:markSeen", {
        params: { groupId },
        body: messageIds ? { messageIds } : {},
      });
      dispatch({ type: GROUP_MARK_SEEN });
    } catch (_) {
      // optional
    }
  };

export const markGroupsReadBulk =
  (groupIds, read = true) =>
  async (dispatch) => {
    if (!groupIds?.length) return;
    dispatch({ type: GROUP_BULK_MARK, payload: { groupIds, read } });
    await axiosInstance.post(
      `${API}groupChats/bulk/${read ? "markSeen" : "markUnread"}`,
      { groupIds },
    );
  };

export const fetchArchivedGroups = () => async () => {
  const payload = await emitWithCallback("group:getList", {
    query: { archived: true, page: 1, limit: 100 },
  });
  return payload?.body?.items ?? [];
};

export const markAllGroupsRead = () => async () => {
  await axiosInstance.post(`${API}groupChats/bulk/markSeen`, { all: true });
};

export const muteGroupsBulk =
  (groupIds, mute = true) =>
  async (dispatch) => {
    if (!groupIds?.length) return;
    dispatch({ type: GROUP_BULK_MUTE, payload: { groupIds, mute } });
    await axiosInstance.post(`${API}groupChats/bulk/mute`, { groupIds, mute });
  };

export const archiveGroupsBulk =
  (groupIds, archive = true) =>
  async (dispatch) => {
    if (!groupIds?.length) return;
    dispatch({ type: GROUP_BULK_REMOVE, payload: { groupIds } });
    await axiosInstance.post(`${API}groupChats/bulk/archive`, {
      groupIds,
      archive,
    });
  };

export const deleteGroupsBulk = (groupIds) => async (dispatch) => {
  if (!groupIds?.length) return;
  dispatch({ type: GROUP_BULK_REMOVE, payload: { groupIds } });
  await axiosInstance.post(`${API}groupChats/bulk/delete`, { groupIds });
};

export const leaveGroupsBulk = (groupIds) => async (dispatch) => {
  if (!groupIds?.length) return;
  dispatch({ type: GROUP_BULK_REMOVE, payload: { groupIds } });
  await axiosInstance.post(`${API}groupChats/bulk/leave`, { groupIds });
};

export const reactGroupMessage =
  (groupId, messageId, emoji) => async (dispatch) => {
    try {
      const payload = await emitWithCallback("group:toggleReaction", {
        params: { groupId: String(groupId), messageId: String(messageId) },
        body: { emoji },
      });
      if (payload?.status && payload?.body) {
        dispatch({ type: GROUP_MESSAGE_REACTION, payload: payload.body });
      }
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

export const resetGroupChat = () => (dispatch) => {
  dispatch({ type: GROUP_RESET });
};

/**
 * Register socket listeners for group chat. Call once when Chat page mounts (same as user chat).
 */
export const registerGroupSocketListeners = (dispatch, getState) => {
  const socket = getSocketUserChat();
  if (!socket) return () => {};

  const onNewMessage = (payload) => {
    dispatch({ type: GROUP_NEW_MESSAGE, payload });
  };
  const onCallActive = (payload) => {
    if (payload?.groupId && payload?.roomName) {
      dispatch({ type: GROUP_CALL_ACTIVE, payload });
    }
  };
  const onCallEnded = (payload) => {
    if (payload?.groupId) {
      dispatch({ type: GROUP_CALL_ENDED, payload });
    }
  };
  const onMessagesBulkDeleted = (payload) => {
    if (payload?.groupId && payload?.messageIds?.length) {
      dispatch({
        type: GROUP_BULK_DELETE_MESSAGES_SUCCESS,
        payload: {
          body: {
            groupId: payload.groupId,
            deletedIds: payload.messageIds,
            hard: payload.hard === true,
          },
        },
      });
    }
  };

  socket.on("group:newMessage", onNewMessage);
  socket.on("group:callActive", onCallActive);
  socket.on("group:callEnded", onCallEnded);
  socket.on("group:bulkMessagesDeleted", onMessagesBulkDeleted);
  socket.on("group:seen", (payload) => {
    if (payload?.groupId && payload?.userId != null) {
      dispatch({ type: GROUP_REMOTE_SEEN, payload });
    }
  });
  socket.on("group:messageReaction", (payload) => {
    if (payload?.groupId && payload?.messageId != null) {
      dispatch({ type: GROUP_MESSAGE_REACTION, payload });
    }
  });
  socket.on("unread:group", (payload) => {
    if (payload?.byGroupId) {
      dispatch({
        type: GROUP_SET_UNREAD_COUNTS,
        payload: payload.byGroupId,
      });
    }
  });

  return () => {
    socket.off("group:newMessage", onNewMessage);
    socket.off("group:callActive", onCallActive);
    socket.off("group:callEnded", onCallEnded);
    socket.off("group:bulkMessagesDeleted", onMessagesBulkDeleted);
    socket.off("group:seen");
    socket.off("group:messageReaction");
    socket.off("unread:group");
  };
};

export const startGroupCall =
  (groupId, topicId, callType = "audio", startedByName) =>
  (dispatch) => {
    const topicPart = topicId != null ? String(topicId) : "general";
    const roomName = `group-${groupId}-topic-${topicPart}`;
    emitWhenConnected("group:call:start", {
      groupId: String(groupId),
      topicId: topicId != null ? String(topicId) : undefined,
      roomName,
      callType,
      startedByName: startedByName || "Someone",
    }).catch(() => {});
  };

export const endGroupCall = (groupId, topicId) => (dispatch) => {
  emitWhenConnected("group:call:end", {
    groupId: String(groupId),
    topicId: topicId != null ? String(topicId) : undefined,
  }).catch(() => {});
};

export const toggleGroupPublic = (groupId, isPublic) => async (dispatch) => {
  const payload = await emitWithCallback("group:togglePublic", {
    params: { groupId: String(groupId) },
    body: { isPublic: !!isPublic },
  });
  dispatch({ type: GROUP_TOGGLE_PUBLIC_SUCCESS, payload });
  return payload;
};

export const generateGroupLink = (groupId) => async (dispatch) => {
  const payload = await emitWithCallback("group:generateLink", {
    params: { groupId: String(groupId) },
  });
  dispatch({ type: GROUP_GENERATE_LINK_SUCCESS, payload });
  return payload;
};

export const getGroupPreviewByLink = (linkId) => async (dispatch) => {
  dispatch({ type: GROUP_PREVIEW_BY_LINK_REQUEST });
  try {
    const payload = await emitWithCallback("group:previewByLink", {
      params: { linkId },
    });
    dispatch({ type: GROUP_PREVIEW_BY_LINK_SUCCESS, payload });
    return payload;
  } catch (err) {
    dispatch({ type: GROUP_PREVIEW_BY_LINK_FAIL, payload: err?.message });
    throw err;
  }
};

export const joinGroupByLink = (linkId) => async (dispatch) => {
  const payload = await emitWithCallback("group:joinByLink", {
    params: { linkId },
  });
  dispatch({ type: GROUP_JOIN_BY_LINK_SUCCESS, payload });
  return payload;
};

export const getGroupJoinRequests = (groupId) => async (dispatch) => {
  const payload = await emitWithCallback("group:getJoinRequests", {
    params: { groupId: String(groupId) },
  });
  dispatch({ type: GROUP_JOIN_REQUESTS_SUCCESS, payload });
  return payload;
};

export const processGroupJoinRequest = (groupId, requestId, action) => async (dispatch) => {
  const payload = await emitWithCallback("group:processJoinRequest", {
    params: { groupId: String(groupId), requestId: String(requestId) },
    body: { action },
  });
  dispatch({ type: GROUP_PROCESS_JOIN_REQUEST_SUCCESS, payload });
  return payload;
};

export const updateGroupInfo = (groupId, { name, description }) => async (dispatch) => {
  const payload = await emitWithCallback("group:updateInfo", {
    params: { groupId: String(groupId) },
    body: { name, description },
  });
  dispatch({ type: GROUP_UPDATE_INFO_SUCCESS, payload });
  return payload;
};
